/**
 * Client-Side Google Ads Conversion Tracking for Outfame
 *
 * ============================================================================
 * ARCHITECTURE OVERVIEW (Last verified: Feb 2026)
 * ============================================================================
 *
 * This file handles CLIENT-SIDE tracking via gtag. It works in tandem with
 * server-side tracking (google-ads-server.ts) using DEDUPLICATION:
 *
 *   Client (this file):  gtag('event', 'conversion', { transaction_id: subscriptionId })
 *   Server (CAPI):       uploadClickConversions({ orderId: subscriptionId })
 *
 *   → Google deduplicates by transaction_id/orderId within 48 hours
 *   → Only ONE conversion is counted, whichever arrives first
 *
 * WHY DUAL TRACKING:
 * - Client-side: Faster, better attribution chain
 * - Server-side: Bypasses ad blockers (10-30% of users)
 * - Combined: Maximum conversion capture with zero double-counting
 *
 * ============================================================================
 * PREDICTED LTV STRATEGY (CRITICAL FOR SMART BIDDING)
 * ============================================================================
 *
 * We do NOT send raw Chargebee payment amounts to Google. Instead, we send
 * PREDICTED LIFETIME VALUE (LTV) based on tier and expected retention:
 *
 *   Formula: Mo1 price + (35% survival rate × Mo2 price)
 *
 *   Computed from src/constants/pricing.ts (first month + 35% × month-2 price,
 *   rounded UP to the next $5). Since the 2026-10-24 parity prices:
 *   | Plan  | First Month | Full Price | Predicted LTV | Reported Value |
 *   |-------|-------------|------------|---------------|----------------|
 *   | Go    | $49         | $79        | $49 + 35%×$79 | $80            |
 *   | Grow  | $69         | $99        | $69 + 35%×$99 | $105           |
 *   | Glory | $129        | $129       | $129+35%×$129 | $175           |
 *
 * WHY THIS MATTERS:
 * - Smart Bidding optimizes for CONVERSION VALUE, not just conversions
 * - Reporting $49 would make Go tier users look as valuable as Glory tier
 * - LTV reporting teaches Google which user segments have highest value
 * - Calibrated against Upgrow's $122 avg value over $3.97M ad spend
 *
 * IMPORTANT: Google Ads "All conv. value" will NOT match Chargebee revenue.
 * This is intentional. They measure different things:
 * - Google Ads: Predicted LTV (for bid optimization)
 * - Chargebee: Actual collected revenue
 *
 * ============================================================================
 * CONVERSION ACTIONS
 * ============================================================================
 *
 * | Action   | When Fired              | Value Sent        | Primary? |
 * |----------|-------------------------|-------------------|----------|
 * | Purchase | Payment success         | getPredictedLTV() | YES      |
 * | Signup   | Email verification done | $0                | No       |
 *
 * Signup is tracked for funnel analysis but has $0 value to avoid
 * confusing Smart Bidding about what constitutes actual revenue.
 *
 * ============================================================================
 * ENHANCED CONVERSIONS
 * ============================================================================
 *
 * We send hashed email (sha256_email_address) with every conversion for
 * Enhanced Conversions matching. This improves match rates by 20-40%.
 *
 * Gmail addresses are normalized before hashing:
 * - Remove dots: john.doe@gmail.com → johndoe@gmail.com
 * - Remove plus suffix: john+test@gmail.com → john@gmail.com
 * - Normalize domain: googlemail.com → gmail.com
 *
 * ============================================================================
 */

import { FIRST_MONTH_PRICES_CENTS, MONTHLY_PRICES_CENTS, type PriceTier } from "@/constants/pricing"

// Outfame Google Ads IDs
const GOOGLE_ADS_ID = 'AW-16655696043'

const CONVERSIONS = {
  purchase: `${GOOGLE_ADS_ID}/FjFjCKyj57gaEKuBh4Y-`,
  signup: `${GOOGLE_ADS_ID}/7CX0CPHVndwbEKuBh4Y-`,
  start_trial: `${GOOGLE_ADS_ID}/G7hcCMyp3v8bEKuBh4Y-`,
} as const

type ConversionType = keyof typeof CONVERSIONS

interface ConversionParams {
  value?: number
  currency?: string
  transaction_id?: string
  email?: string // For Enhanced Conversions
}

/**
 * Normalize Gmail addresses for Enhanced Conversions matching
 * Gmail ignores dots in usernames and plus-suffixes, so we normalize:
 * - john.doe+newsletter@gmail.com → johndoe@gmail.com
 * - j.o.h.n@googlemail.com → john@gmail.com
 *
 * This improves Enhanced Conversions match rate by 10-20% for Gmail users.
 */
function normalizeGmailAddress(email: string): string {
  const atIndex = email.indexOf('@')
  if (atIndex === -1) return email

  const localPart = email.substring(0, atIndex)
  const domain = email.substring(atIndex + 1).toLowerCase()

  // Only normalize for gmail.com and googlemail.com domains
  if (domain !== 'gmail.com' && domain !== 'googlemail.com') {
    return email
  }

  // Remove dots from local part
  let normalized = localPart.replace(/\./g, '')

  // Remove plus suffix (everything after +)
  const plusIndex = normalized.indexOf('+')
  if (plusIndex !== -1) {
    normalized = normalized.substring(0, plusIndex)
  }

  // Always use gmail.com (googlemail.com is equivalent)
  return `${normalized}@gmail.com`
}

/**
 * Hash email using SHA-256 for Enhanced Conversions
 * Google requires lowercase, trimmed, SHA-256 hashed email
 */
async function hashEmail(email: string): Promise<string> {
  // First normalize and lowercase
  let normalized = email.trim().toLowerCase()

  // Apply Gmail-specific normalization for better matching
  normalized = normalizeGmailAddress(normalized)

  const encoder = new TextEncoder()
  const data = encoder.encode(normalized)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Track a Google Ads conversion event with Enhanced Conversions support
 * @param conversionType - Type of conversion (e.g., 'purchase', 'signup')
 * @param params - Conversion parameters (value, currency, transaction_id, email)
 */
export async function trackGoogleAdsConversion(
  conversionType: ConversionType,
  params?: ConversionParams
): Promise<void> {
  // Production-only gate
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Google Ads] Dev mode - would track:', conversionType, params)
    return
  }

  // SSR safety
  if (typeof window === 'undefined') return

  // Check if gtag is loaded
  const gtag = (window as Window & { gtag?: (...args: any[]) => void }).gtag
  if (!gtag) {
    console.warn('[Google Ads] gtag not loaded')
    return
  }

  const sendTo = CONVERSIONS[conversionType]
  if (!sendTo) {
    console.warn('[Google Ads] Unknown conversion type:', conversionType)
    return
  }

  try {
    // Build conversion data
    const conversionData: Record<string, any> = {
      send_to: sendTo,
      value: params?.value || 0,
      currency: params?.currency || 'USD',
      transaction_id: params?.transaction_id,
    }

    // Add Enhanced Conversions data if email provided
    if (params?.email) {
      const hashedEmail = await hashEmail(params.email)
      conversionData.user_data = {
        sha256_email_address: hashedEmail
      }
      console.log('[Google Ads] Enhanced Conversions enabled with hashed email')
    }

    gtag('event', 'conversion', conversionData)
    console.log('[Google Ads] Conversion tracked:', conversionType, {
      ...params,
      email: params?.email ? '[REDACTED]' : undefined
    })
  } catch (error) {
    console.error('[Google Ads] Error tracking conversion:', error)
  }
}

/**
 * PROBABILITY-WEIGHTED LIFETIME VALUE (LTV) STRATEGY
 *
 * Formula: Mo1 price + (35% survival rate × Mo2 price)
 * Calibrated against Upgrow's proven $122 avg value over $3.97M in Google Ads spend.
 *
 * Inputs come from src/constants/pricing.ts (first-month + month-2 price per tier),
 * rounded UP to the next $5 — the same rounding that produced the original
 * $120/$175/$270 from the pre-parity $199/$299/$399 ladder. Parity prices
 * (2026-10-24, Go $49→$79, Grow $69→$99, Glory $129→$129):
 *   Go:    $49 + (0.35 × $79)  = $76.65  → $80
 *   Grow:  $69 + (0.35 × $99)  = $103.65 → $105
 *   Glory: $129 + (0.35 × $129) = $174.15 → $175
 *
 * Adjust survival % as real retention data accumulates.
 *
 * MUST stay in sync with server-side version in `google-ads-server.ts`.
 * If tiers diverge, Smart Bidding gets inconsistent signals.
 */
const LTV_MONTH2_SURVIVAL = 0.35
function predictedLtvForTier(tier: PriceTier): number {
  const dollars = (FIRST_MONTH_PRICES_CENTS[tier] + LTV_MONTH2_SURVIVAL * MONTHLY_PRICES_CENTS[tier]) / 100
  return Math.ceil(dollars / 5) * 5
}
export function getPredictedLTV(amountDollars: number): number {
  if (!amountDollars || isNaN(amountDollars)) return 0
  if (amountDollars <= 0) return 0
  if (amountDollars <= FIRST_MONTH_PRICES_CENTS.go / 100) return predictedLtvForTier("go")
  if (amountDollars <= FIRST_MONTH_PRICES_CENTS.grow / 100) return predictedLtvForTier("grow")
  if (amountDollars <= FIRST_MONTH_PRICES_CENTS.glory / 100) return predictedLtvForTier("glory")
  return predictedLtvForTier("glory")  // Higher amounts (annual plans, etc.) -> Glory estimate
}

/*
 * GOOGLE ADS DAY-0 CONVERSION VALUE — recalibrated 2026-08-01, yearly split 2026-08-04
 *
 * Monthly bands derived from realized 90-day $/signup on matured Jan–May 2026
 * Google Ads cohorts, scaled so the blended Day-0 value matches the old
 * predicted-LTV blend (spend-neutral for tROAS). GOOGLE ADS ONLY — Meta/TikTok
 * stay on getPredictedLTV().
 *
 *   Go    $49  → $88
 *   Grow  $69  → $294
 *   Glory $129 → $197
 *
 * YEARLY (> $129): ~70% of yearly signups are CC TRIALS ($0 collected at Day-0,
 * ~96% retracted at Day-3), so firing the full $348–1188 charged amount created a
 * rolling phantom-value window. Day-0 yearly value is now an EXPECTATION:
 *   amount × 0.04  (measured yearly first-charge approval 4.0%, N=2985, 2026;
 *                   revisit if approval shifts).
 * EXCEPTION — cash-collected yearly: a call site that fires AFTER a successful
 * REAL charge of that amount (pay-upfront yearly success, renewal first-purchase)
 * passes cashCollected=true and sends the FULL charged amount. CC-trial paths
 * ($0 collected) leave the default (false). The Day-3 trial-conversion path
 * restates the Day-0 conversions UP to the full amount when a yearly trial pays.
 *
 * MUST stay value-identical with the server version in `google-ads-server.ts`
 * (Google dedups client+server on the same conversion action + orderId;
 * divergent values corrupt the Smart Bidding signal).
 */
export function getGoogleAdsConversionValue(amountDollars: number, cashCollected: boolean = false): number {
  if (!amountDollars || isNaN(amountDollars)) return 0
  if (amountDollars <= 0) return 0
  if (amountDollars <= 49) return 88    // Go tier
  if (amountDollars <= 69) return 294   // Grow tier
  if (amountDollars <= 129) return 197  // Glory tier
  // Yearly: full charged amount only when cash was really collected, else the expectation.
  if (cashCollected) return Math.round(amountDollars)
  return Math.round(amountDollars * 0.04)
}

/**
 * Track a purchase conversion with Enhanced Conversions
 *
 * DEDUPLICATION: transaction_id = subscriptionId is the dedup key.
 * The server-side Chargebee webhook (google-ads-server.ts) also uploads this
 * same conversion with orderId = subscriptionId to the same conversion action.
 * Google deduplicates by (conversionAction + orderId/transaction_id),
 * so only ONE conversion is counted regardless of which fires first.
 *
 * The server-side upload acts as a safety net for ad-blocked browsers.
 * sessionStorage guard in the caller prevents double-firing within the same tab.
 */
export async function trackPurchase(
  value: number,
  subscriptionId: string,
  email?: string,
  cashCollected: boolean = false,
  currency: string = 'USD'
): Promise<void> {
  const ltvValue = getGoogleAdsConversionValue(value, cashCollected)
  await trackGoogleAdsConversion('purchase', {
    value: ltvValue,
    currency,
    transaction_id: subscriptionId,
    email,
  })
}

/**
 * Track a signup conversion
 *
 * Fires with $0 value — tracking only, no value attribution.
 * Used as a secondary signal for Smart Bidding to identify high-intent traffic,
 * but purchase conversion is the primary optimization target.
 */
export async function trackSignup(
  userId: string,
  email?: string
): Promise<void> {
  await trackGoogleAdsConversion('signup', {
    value: 0, // Signup has no direct value
    transaction_id: `signup_${userId}`,
    email,
  })
}

/**
 * Track a CC trial start conversion (observation-only WEBPAGE action)
 *
 * Fires alongside trackPurchase() at Day 0 for CC trial signups.
 * This is a SECONDARY action for analytics — not used for Smart Bidding optimization.
 * The "fire-and-retract" pattern retracts the Purchase conversion at Day 3
 * if the trial converts, but this StartTrial event persists for funnel visibility.
 *
 * Uses the same transaction_id as trackPurchase() (subscriptionId) for correlation,
 * but targets a separate conversion action so retraction doesn't affect it.
 */
export async function trackStartTrial(
  value: number,
  subscriptionId: string,
  email?: string
): Promise<void> {
  await trackGoogleAdsConversion('start_trial', {
    value: getGoogleAdsConversionValue(value, false), // CC trial start — $0 collected, yearly uses the expectation value
    currency: 'USD',
    transaction_id: subscriptionId,
    email,
  })
}
